// DOM-centric Utility Functions
import animatedScrollTo from 'animated-scroll-to'

export const createEvent = (
    type = null,
    eventInit = { bubbles: false, cancelable: true }
) => {
    if (!type)
        return null

    const isCustomEvent = typeof (eventInit || {}).detail === 'object'

    const isEventConstructor = (!isCustomEvent
            && typeof window.Event === 'function')
        || (isCustomEvent
            && typeof window.CustomEvent === 'function')

    const event = isEventConstructor
        ? (new window[`${isCustomEvent ? 'Custom' : ''}Event`](type, eventInit))
        : document.createEvent('HTMLEvents')

    if (!isEventConstructor)
        event.initEvent(type,
            (eventInit || {}).bubbles || false,
            (eventInit || {}).cancelable || true)

    if (isCustomEvent
            && !isEventConstructor)
        event.detail = eventInit.detail

    return event
}

export const getScrollbarWidth = () => {
    const inner = document.createElement('p')
    const outer = document.createElement('div')

    inner.style.width = '100%'
    inner.style.height = '200px'

    outer.className = 'scrollbar-width-test'
    outer.style.position = 'absolute'
    outer.style.top = '-150px'
    outer.style.left = '-200px'
    outer.style.width = '200px'
    outer.style.height = '150px'
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll'

    outer.appendChild(inner)
    document.body.appendChild(outer)

    const width = outer.offsetWidth - inner.clientWidth
    document.body.removeChild(outer)

    return width
}

export const isScrollingTo = () => document.documentElement
    .getAttribute('data-scrolling-to') === 'true'

export const scrollTo = async (numberOrArrayOrElement, opts = {}) => {
    if (isScrollingTo())
        return

    document.documentElement
        .setAttribute('data-scrolling-to', true)

    const isArray = Array.isArray(numberOrArrayOrElement)
        && numberOrArrayOrElement
            .filter(number => typeof number === 'number')
            .length === 2

    const isNumber = !isArray
        && typeof numberOrArrayOrElement === 'number'

    const scrollToCoords = isArray
        ? numberOrArrayOrElement
        : [0, isNumber ? numberOrArrayOrElement : 0]

    const el = (numberOrArrayOrElement || {}).nodeType
        ? numberOrArrayOrElement
        : null

    if (!isArray
            && !isNumber
            && !el)
        return Promise.resolve(false)

    if (el) {
        let scrollY = el.getBoundingClientRect().top
            + (window.scrollY || 0)

        const computedStyle = window.getComputedStyle(el)

        const getNumberFromStr = (str) =>
            Number(str.replace(/\D/g, ''))

        if (computedStyle.scrollMarginTop)
            scrollY -= getNumberFromStr(computedStyle.scrollMarginTop) || 0

        if (computedStyle.scrollPaddingTop)
            scrollY -= getNumberFromStr(computedStyle.scrollPaddingTop) || 0

        console.log(scrollY, computedStyle.scrollMarginTop)

        scrollToCoords[1] = scrollY
    }

    const isSafari = window.navigator.userAgent.indexOf('Safari') >= 0
        && window.navigator.userAgent.indexOf('Chrome') === -1

    const focusEl = () => {
        if (!el)
            return

        if (el.tabIndex === -1
                && !el.getAttribute('tabindex'))
            el.setAttribute('tabindex', -1)

        el.focus({ preventScroll: true })
    }

    if (isSafari)
        focusEl()

    const isTouch = document.body.classList.contains('touch')

    if (isTouch
            && !('cancelOnUserAction' in opts))
        opts.cancelOnUserAction = false

    const scrollToOpts = Object.assign({
        easing: t => t < 0.5
            ? 4 * t * t * t
            : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
        speed: 200
    }, opts)

    return Promise.resolve(
        scrollToOpts.speed
            ? animatedScrollTo(scrollToCoords, scrollToOpts)
            : window.scrollTo(scrollToCoords[0], scrollToCoords[1]))
        .then((done) => {
            if (done
                    && !isSafari)
                focusEl()

            document.documentElement
                .removeAttribute('data-scrolling-to')

            return done
        })
}

export const setSiblingsAriaHidden = (
    el,
    hidden = true,
    domRecursion = true
) => {
    const setSiblingAttrs = (_el) => Array.from(_el?.parentElement.children)
        .filter(__el => __el !== el
            && __el !== _el
            && __el.nodeName !== 'SCRIPT'
            && ((hidden
                    && __el.getAttribute('aria-hidden') !== 'true')
                || (!hidden
                    && __el.getAttribute('data-aria-hidden-restore'))))
        .forEach(__el => {
            if (hidden
                    && __el.getAttribute('aria-hidden') !== 'true')
                __el.setAttribute('aria-hidden', true)

            if (hidden
                    && __el.getAttribute('data-aria-hidden-restore') !== 'true')
                __el.setAttribute('data-aria-hidden-restore', true)

            if (!hidden
                    && __el.getAttribute('aria-hidden'))
                __el.removeAttribute('aria-hidden')

            if (!hidden
                    && __el.getAttribute('data-aria-hidden-restore'))
                __el.removeAttribute('data-aria-hidden-restore')
        })

    if (!domRecursion)
        return setSiblingAttrs(el)

    while (el.parentElement !== document.documentElement) {
        setSiblingAttrs(el)
        el = el.parentElement
    }
}
